import {
  Button,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  ModalOverlay,
  UseDisclosureReturn,
  useToast,
} from "@chakra-ui/react";
import React, { FormEvent, ReactNode, useState } from "react";
import { captureRequestError } from "../../helper/captureRequestError";
import { useReportError } from "../../helpers/useReportError";
import { AppModal } from "./AppModal";
import { AppModalContent } from "./AppModalContent";
import { AppModalFooter } from "./AppModalFooter";
import { modalSuccessToastProps } from "./modalToast";

/**
 * A modal asking for confirmation before performing an action, with
 * no need for any form input.
 */
export function ConfirmActionModal({
  disclosure,
  title,
  confirmButtonText,
  children,
  colorScheme = "primary",
  onConfirm,
  successTitle,
}: {
  disclosure: UseDisclosureReturn;
  title: string;
  children: ReactNode;
  confirmButtonText: string;
  colorScheme?: "red" | "primary"; // use red if the action is destructive
  onConfirm: () => Promise<void>;

  // provide a success message if you want a toast to appear on success
  successTitle?: string;
}): React.ReactElement {
  const reportError = useReportError();

  const toast = useToast();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);

      await onConfirm();

      if (successTitle) {
        toast({
          title: successTitle,
          ...modalSuccessToastProps,
        });
      }

      disclosure.onClose();
    } catch (error) {
      captureRequestError(error);
      reportError(error, {
        title: `${title} failed`,
      });
      setIsSubmitting(false);
    }
  };

  return (
    <AppModal
      isOpen={disclosure.isOpen}
      onClose={disclosure.onClose}
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
    >
      <ModalOverlay />
      <AppModalContent as="form" noValidate onSubmit={handleSubmit}>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />

        <ModalBody>{children}</ModalBody>

        <AppModalFooter>
          <Button
            type="button"
            variant="outline"
            colorScheme="gray"
            onClick={disclosure.onClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            colorScheme={colorScheme}
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            type="submit"
          >
            {confirmButtonText}
          </Button>
        </AppModalFooter>
      </AppModalContent>
    </AppModal>
  );
}
